import type { BuffSnapshot } from "@vinyl/shared";
import {
  type Character,
  type CharacterStats,
  type ChatMessage,
  type ChunkData,
  CHUNK_SIZE,
  ENCUMBRANCE_WARN_PCT,
  formatCurrency,
  type EquippedSlots,
  type EquipSlot,
  type Facing,
  findPath,
  getItemTemplate,
  getNpcTemplate,
  getSpellTemplate,
  type HotbarSlots,
  isEncumberedFull,
  type ItemInstance,
  type MobSnapshot,
  MOVE_COOLDOWN_MS,
  MOVE_COOLDOWN_MS_DIAG,
  type NpcSnapshot,
  type PlayerSnapshot,
  type Portal,
  type Position,
  type ProjectileSnapshot,
  type PartyErrorCode,
  type PartySnapshot,
  type QuestErrorCode,
  type QuestLogEntry,
  type SpawnPoint,
  STAT_KEYS,
  type StatKey,
  type TerrainId,
  TILE_H,
  TILE_W,
  type TilemapJson,
  deriveCarryCapacity,
  getGameConfig,
  getPassiveCatalogue,
  getPassiveTemplate,
  bankPts,
  isWalkable,
  type VendorErrorCode,
  type VendorStockEntry,
  xpToReach,
} from "@vinyl/shared";
import { KeyboardController } from "../input/keyboard.js";
import type { GameClient } from "../net/client.js";
import { clearToken } from "../net/storage.js";
import type { AppState, Store } from "../state.js";
import type { AreaEffectSpawnPayload } from "../render/area-effects.js";
import type {
  ProjectileExpirePayload,
  ProjectileSpawnPayload,
} from "../render/projectiles.js";
import { WorldView } from "../render/world-view.js";
import { BubbleManager } from "./bubble.js";
import { ChatPanel } from "./chat.js";
import { DamageNumberManager } from "./damage-numbers.js";
import { DeathOverlay } from "./death-overlay.js";
import { el } from "./dom.js";
import { resetAllWindowPositions } from "./window-chrome.js";
import { BuffTracker } from "./buff-tracker.js";
import { CastBar } from "./cast-bar.js";
import { HotbarBar } from "./hotbar.js";
import { InventoryPanel } from "./inventory.js";
import { LevelUpBanner } from "./level-up-banner.js";
import { GoldFloater } from "./gold-floater.js";
import { LootWindow } from "./loot-window.js";
import { SpellbookPanel } from "./spellbook.js";
import { Toast } from "./toast.js";
import { type VendorCartLines, VendorWindow } from "./vendor-window.js";
import { QuestWindow } from "./quest-window.js";
import { QuestTracker } from "./quest-tracker.js";
import { PartyPanel } from "./party-panel.js";
import { InviteToast } from "./invite-toast.js";
import { XpFloater } from "./xp-floater.js";

const HARD_DESYNC_TILES = 3;
// Auto-chase leash: if a clicked mob runs further than this, give up.
const MAX_CHASE_TILES = 25;

export interface SelfStatsPayload {
  hp: number;
  maxHp: number;
  // M8 — single mana pool.
  mana: number;
  maxMana: number;
  inCombat: boolean;
  lifeState: "alive" | "dead";
  respawnAt: number | null;
  level: number;
  xp: number;
  xpToNext: number;
  unspentPoints: number;
  stats: CharacterStats;
  effectiveStats: CharacterStats;
  defense: number;
  meleeDmgPct: number;
  rangedDmgPct: number;
  spellDmgPct: number;
  critPct: number;
  resistPhysPct: number;
  resistDivinePct: number;
  resistNecromancyPct: number;
  resistWaterPct: number;
  resistFirePct: number;
  resistAirPct: number;
  resistEarthPct: number;
  copper: number;
  currentWeight: number;
  maxWeight: number;
  // M17.4 — ids of dons unlocked by the current base stats.
  unlockedPassives: string[];
}

export interface XpGainedPayload {
  amount: number;
  source: "kill" | "death" | "quest";
  mobId?: string;
  questId?: string;
}

export interface GoldGainedPayload {
  amount: number; // copper
  source: "kill" | "quest";
  mobId?: string;
  questId?: string;
}

export interface InventoryUpdatePayload {
  inventory: ItemInstance[];
  equipped: EquippedSlots;
}

export interface HotbarUpdatePayload {
  hotbar: HotbarSlots;
  knownSpellIds: string[];
}

export interface CastStateUpdatePayload {
  spellId: string | null;
  castEndsAt: number | null;
  castDurationMs: number;
}

export interface BuffUpdatePayload {
  playerId: string;
  buffs: BuffSnapshot[];
}

export interface GroundBagsUpdatePayload {
  zoneId: string;
  bags: { bagId: string; x: number; y: number; itemCount: number; expiresAt: number }[];
}

export interface LootContentsPayload {
  bagId: string;
  items: ItemInstance[];
}

export interface NpcsInZonePayload {
  zoneId: string;
  npcs: NpcSnapshot[];
}

export interface VendorPanelPayload {
  npcId: string;
  templateId: string;
  stock: VendorStockEntry[];
  sellMultiplier: number;
}

export interface VendorResultPayload {
  npcId: string;
  copperDelta: number;
}

export interface NpcSpeechPayload {
  npcId: string;
  text: string;
  durationMs?: number;
}

export interface NpcDialogPayload {
  npcId: string;
  templateId: string;
  available: { questId: string }[];
  active: { questId: string; progress: number[] }[];
  completable: { questId: string; progress: number[] }[];
}

export interface QuestLogUpdatePayload {
  questLog: QuestLogEntry[];
}

export interface PartyUpdatePayload {
  party: PartySnapshot | null;
}

export interface PartyInviteIncomingPayload {
  fromCharacterId: string;
  fromCharacterName: string;
  expiresAt: number;
}

export interface LevelUpPayload {
  newLevel: number;
  unspentPoints: number;
  maxHp: number;
  maxMana: number;
}

export interface CombatHitPayload {
  attackerId: string;
  attackerKind: "player" | "mob";
  targetId: string;
  targetKind: "player" | "mob";
  damage: number;
  crit: boolean;
  source: "melee" | "ranged";
}

export interface CombatHealPayload {
  casterId: string;
  targetCharacterId: string;
  amount: number;
  spellId: string;
}

export type ProjectileSpawnedPayload = ProjectileSpawnPayload;
export type ProjectileExpiredPayload = ProjectileExpirePayload;
export type AreaEffectSpawnedPayload = AreaEffectSpawnPayload;
export interface AreaEffectExpiredPayload {
  areaId: string;
  zoneId: string;
}

export interface EntityDiedPayload {
  id: string;
  kind: "player" | "mob";
  killerId: string | null;
  killerKind: "player" | "mob" | null;
}

export interface EntityRespawnedPayload {
  id: string;
  kind: "player" | "mob";
}

// M11 — zoneEnter is now metadata-only; terrain arrives via onChunkData.
export interface ZoneEnterMessage {
  zoneId: string;
  width: number;
  height: number;
  isSafe: boolean;
  spawnPoints: SpawnPoint[];
  portals: Portal[];
}

export interface InGameView {
  root: HTMLElement;
  update(state: AppState): void;
  onZoneEnter(
    meta: ZoneEnterMessage,
    selfCharacterId: string,
    pos: Position,
    facing: Facing,
  ): void;
  onChunkData(chunk: ChunkData & { zoneId: string }): void;
  onSnapshot(snapshot: {
    players: PlayerSnapshot[];
    mobs: MobSnapshot[];
    projectiles: ProjectileSnapshot[];
    serverTime: number;
  }): void;
  onMobDebugSnapshot(payload: {
    zoneId: string;
    mobs: { id: string; losMissCount: number; swingAt: number }[];
  }): void;
  onChatPosted(msg: ChatMessage): void;
  onMoveRejected(serverX: number, serverY: number): void;
  onCombatHit(payload: CombatHitPayload): void;
  onCombatHeal(payload: CombatHealPayload): void;
  onEntityDied(payload: EntityDiedPayload): void;
  onEntityRespawned(payload: EntityRespawnedPayload): void;
  onSelfStats(payload: SelfStatsPayload): void;
  onDonUsed(payload: { donId: string; x: number; y: number }): void;
  onXpGained(payload: XpGainedPayload): void;
  onGoldGained(payload: GoldGainedPayload): void;
  onLevelUp(payload: LevelUpPayload): void;
  onInventoryUpdate(payload: InventoryUpdatePayload): void;
  onEquipError(reason: string): void;
  onHotbarUpdate(payload: HotbarUpdatePayload): void;
  onCastStateUpdate(payload: CastStateUpdatePayload): void;
  onCastError(reason: string): void;
  onBuffUpdate(payload: BuffUpdatePayload): void;
  onGroundBagsUpdate(payload: GroundBagsUpdatePayload): void;
  onLootContents(payload: LootContentsPayload): void;
  onProjectileSpawned(payload: ProjectileSpawnedPayload): void;
  onProjectileExpired(payload: ProjectileExpiredPayload): void;
  onAreaEffectSpawned(payload: AreaEffectSpawnedPayload): void;
  onAreaEffectExpired(payload: AreaEffectExpiredPayload): void;
  onNpcsInZone(payload: NpcsInZonePayload): void;
  onVendorPanel(payload: VendorPanelPayload): void;
  onVendorResult(payload: VendorResultPayload): void;
  onVendorError(code: VendorErrorCode): void;
  onNpcSpeech(payload: NpcSpeechPayload): void;
  onNpcDialog(payload: NpcDialogPayload): void;
  onQuestLogUpdate(payload: QuestLogUpdatePayload): void;
  onQuestError(code: QuestErrorCode): void;
  onPartyUpdate(payload: PartyUpdatePayload): void;
  onPartyInviteIncoming(payload: PartyInviteIncomingPayload): void;
  onPartyError(code: PartyErrorCode): void;
  onRateLimited(retryAfterMs: number): void;
  // M7.1 — fires after a catalogueUpdate landed and the runtime registry
  // was swapped. Lets the view re-render anything that resolves an
  // ItemTemplate (currently: inventory panel tooltips). Optional so we can
  // wire it lazily as catalogues grow in M7.2.
  refreshAfterCatalogueUpdate?(): void;
  destroy(): void;
}

export function createInGameView(store: Store, client: GameClient): InGameView {
  const screen = store.get().screen;
  if (screen.kind !== "in_game") throw new Error("createInGameView: wrong screen");
  const character = screen.character;

  const root = el("div", { class: "game-root" });

  const onlineNames = el("span", { class: "online-names" }, "—");
  const presenceText = document.createTextNode("0 online: ");
  const presence = el("div", { class: "presence" });
  presence.append(presenceText, onlineNames);

  const deleteBtn = el(
    "button",
    { class: "btn-ghost btn-danger", title: "Delete this character permanently" },
    "Delete character",
  );
  deleteBtn.addEventListener("click", () => {
    const name = character.name;
    const ok = window.confirm(
      `Delete "${name}"? This will permanently remove the character (inventory, equipment, level, position). The account stays — you'll be sent to character creation.`,
    );
    if (!ok) return;
    client.send({ t: "deleteCharacter" });
  });

  const devLevelBtn = el(
    "button",
    {
      class: "btn-ghost btn-dev",
      title: "DEV: grant 1 level (+5 stat points, full heal). Removed before release.",
    },
    "+1 level (dev)",
  );
  devLevelBtn.addEventListener("click", () => {
    client.send({ t: "devGrantLevel" });
  });

  const resetUiBtn = el(
    "button",
    {
      class: "btn-ghost",
      title: "Reset all HUD window positions to their defaults",
    },
    "Reset UI",
  );
  resetUiBtn.addEventListener("click", () => {
    resetAllWindowPositions();
  });

  const logoutBtn = el("button", { class: "btn-ghost" }, "Logout");
  logoutBtn.addEventListener("click", () => {
    clearToken();
    client.disconnect();
    location.reload();
  });
  const header = el(
    "header",
    { class: "topbar" },
    el("div", { class: "brand" }, "Vinyl"),
    presence,
    devLevelBtn,
    resetUiBtn,
    deleteBtn,
    logoutBtn,
  );

  const worldMount = el("main", { class: "world-stage world-mount" });
  const sheet = createSheet(character, {
    onAllocate: (stat) => client.send({ t: "allocateStat", d: { stat, amount: 1 } }),
  });

  const deathOverlay = new DeathOverlay();
  const levelUpBanner = new LevelUpBanner();
  const toast = new Toast();
  const inventoryPanel = new InventoryPanel({
    onEquip: (instanceId) => client.send({ t: "equipItem", d: { instanceId } }),
    onUnequip: (slot: EquipSlot) => client.send({ t: "unequipItem", d: { slot } }),
    onUse: (instanceId) => client.send({ t: "useItem", d: { instanceId } }),
  });
  const spellbookPanel = new SpellbookPanel({
    onAssignToHotbar: (slot, spellId) =>
      client.send({ t: "setHotbarSlot", d: { slot, spellId } }),
  });
  const hotbar = new HotbarBar({
    onSetSlot: (slot, spellId) =>
      client.send({ t: "setHotbarSlot", d: { slot, spellId } }),
    // Clicking a filled hotbar slot toggles spell-mode (T4C-style) — the
    // next mob click resolves the cast.
    onCast: (slot) => enterSpellMode(slot),
  });
  const castBar = new CastBar();
  const buffTracker = new BuffTracker();
  // Bag the user has clicked on and wants to open. We send `openLoot` only
  // once we're adjacent (Chebyshev ≤ 1); the auto-walk handles the approach.
  let pendingBagOpen: string | null = null;
  // The bag whose contents are currently displayed in the loot window.
  let openBagId: string | null = null;
  const lootWindow = new LootWindow({
    onTakeOne: (bagId, instanceId) =>
      client.send({ t: "takeLoot", d: { bagId, instanceId } }),
    onTakeAll: (bagId) => client.send({ t: "takeAllLoot", d: { bagId } }),
    onClose: () => {
      openBagId = null;
      pendingBagOpen = null;
    },
  });
  // M5.1 — vendor modal. pendingNpcOpen mirrors pendingBagOpen for the
  // walk-to-NPC-then-open flow. inventoryCache + copperCache feed the modal
  // so the sell column + arrow-disable predicates can render immediately on
  // open without waiting for a fresh selfStats / inventoryUpdate roundtrip.
  let pendingNpcOpen: string | null = null;
  let openVendorNpcId: string | null = null;
  let inventoryCache: ItemInstance[] = character.inventory ?? [];
  let equippedCache = character.equipped ?? {};
  let copperCache = character.copper ?? 0;
  const vendorWindow = new VendorWindow({
    onValidate: (npcId, lines: VendorCartLines) => {
      client.send({ t: "vendorTransact", d: { npcId, buy: lines.buy, sell: lines.sell } });
    },
    onClose: () => {
      if (openVendorNpcId) {
        client.send({ t: "vendorClose" });
      }
      openVendorNpcId = null;
      pendingNpcOpen = null;
    },
  });
  let openQuestNpcId: string | null = null;
  const questTracker = new QuestTracker();
  questTracker.setEntries(character.questLog ?? []);
  // M5.3 — party HUD + invite popup. Party state is non-persistent (resets
  // on server restart) so we start with no party regardless of the
  // character cache; the enterWorld `partyUpdate` packet repopulates if a
  // party survived the reconnect.
  const partyPanel = new PartyPanel(character.id, {
    onLeave: () => client.send({ t: "partyLeave" }),
    onKick: (characterId) => client.send({ t: "partyKick", d: { characterId } }),
    onPromote: (characterId) =>
      client.send({ t: "partyPromote", d: { characterId } }),
  });
  const inviteToast = new InviteToast({
    onAccept: () => client.send({ t: "partyInviteAccept" }),
    onDecline: () => client.send({ t: "partyInviteDecline" }),
  });
  const questWindow = new QuestWindow({
    onAccept: (npcId, questId) => client.send({ t: "questAccept", d: { npcId, questId } }),
    onAbandon: (questId) => client.send({ t: "questAbandon", d: { questId } }),
    onTurnIn: (npcId, questId) => client.send({ t: "questTurnIn", d: { npcId, questId } }),
    onClose: () => {
      openQuestNpcId = null;
      pendingNpcOpen = null;
    },
  });
  worldMount.append(
    deathOverlay.root,
    levelUpBanner.root,
    toast.root,
    inventoryPanel.root,
    spellbookPanel.root,
    lootWindow.root,
    vendorWindow.root,
    questWindow.root,
    questTracker.root,
    partyPanel.root,
    inviteToast.root,
    castBar.root,
    hotbar.root,
    buffTracker.root,
  );

  root.append(header, worldMount, sheet.root);

  let tilemap: TilemapJson | null = null;
  let selfId: string | null = null;
  let lastMoveAt = 0;
  // Latest known carried weight + capacity, fed by selfStats. Used to gate
  // movement at ≥100 % capacity and to scale the prediction cooldown.
  let currentWeight = 0;
  let maxWeight = deriveCarryCapacity(character.stats);
  // M17.4 — null until the first selfStats seeds it; then drives unlock toasts.
  let lastUnlockedPassives: Set<string> | null = null;
  let chatBlocked = false;
  let pointerHeld = false;
  let pointerTargetTile: { tx: number; ty: number } | null = null;
  let pointerStartTile: { tx: number; ty: number } | null = null;
  let pointerStartedAt = 0;
  let pointerInFollowMode = false;
  const HOLD_TO_FOLLOW_MS = 180;
  // Reconcile silently with the server for a short window after a local
  // move — covers the round-trip latency between predict and ack.
  const RECONCILE_GRACE_MS = MOVE_COOLDOWN_MS * 2;

  const mobsByTile = new Map<string, { id: string; templateId: string }>();
  const mobsById = new Map<string, MobSnapshot>();
  const playersByTile = new Map<string, string>(); // tileKey → characterId
  const bagsByTile = new Map<string, string>(); // tileKey → bagId
  // M5.1 — npc click index, mirrors bagsByTile / bagsById.
  const npcsByTile = new Map<string, string>(); // tileKey → npcId
  const npcsById = new Map<string, { x: number; y: number; templateId: string }>();
  const bagsById = new Map<string, { x: number; y: number; itemCount: number }>();
  let currentTargetMobId: string | null = null;
  let isDead = false;

  // T4C spell-mode: when set, the player is choosing a target for this
  // hotbar slot. Cursor flips to crosshair, click-on-mob casts at that mob
  // (bypassing auto-walk), click-on-ground exits mode.
  let pendingSpellSlot: number | null = null;
  // Tracks whether the server says we're actively casting / in an auto-cast
  // loop. Driven by castStateUpdate. Used to decide if ESC should send a
  // cancelCast (active stance) or just exit the pre-cast spell-mode.
  let castActive = false;
  function enterSpellMode(slot: number): void {
    if (pendingSpellSlot === slot) {
      exitSpellMode();
      return;
    }
    pendingSpellSlot = slot;
    hotbar.setActiveSlot(slot);
    root.classList.add("spell-mode");
  }
  function exitSpellMode(): void {
    if (pendingSpellSlot === null) return;
    pendingSpellSlot = null;
    hotbar.setActiveSlot(null);
    root.classList.remove("spell-mode");
  }

  // Click-to-move queue: deltas (dx, dy) the local player should walk through,
  // computed client-side via shared findPath. Drained at MOVE_COOLDOWN_MS pace
  // by the keyboard controller's fallback hook. Cleared by any WASD input,
  // a blocked move, or a zone change.
  let clickPath: { dx: -1 | 0 | 1; dy: -1 | 0 | 1 }[] = [];

  const tileKey = (tx: number, ty: number): string => `${tx},${ty}`;

  const worldView = new WorldView(worldMount, {
    onPointerDown: (tx, ty) => {
      if (!tilemap) return;
      pointerHeld = true;
      pointerStartTile = { tx, ty };
      pointerTargetTile = { tx, ty };
      pointerStartedAt = performance.now();
      pointerInFollowMode = false;
    },
    onPointerMove: (tx, ty) => {
      if (!pointerHeld) return;
      pointerTargetTile = { tx, ty };
      if (pointerStartTile && (tx !== pointerStartTile.tx || ty !== pointerStartTile.ty)) {
        pointerInFollowMode = true;
      }
    },
    onPointerUp: () => {
      const wasSingleClick = !pointerInFollowMode;
      const target = pointerTargetTile;
      pointerHeld = false;
      pointerTargetTile = null;
      pointerStartTile = null;
      pointerInFollowMode = false;
      if (!wasSingleClick || !target || !tilemap || isDead) return;
      const mobOnTile = mobsByTile.get(tileKey(target.tx, target.ty));
      // Spell-mode click — if the player is choosing a target, route the
      // click into a cast (no auto-walk, no melee attack). We intentionally
      // do NOT set currentTargetMobId: that field drives `maybeChaseTarget`,
      // which would auto-walk the player into melee — defeating the whole
      // point of a ranged spell. The visual target ring stays via
      // setTargetMob; if the cast errors out (out of range etc.), the
      // server's castError toast tells the user, and they can step closer
      // manually.
      if (pendingSpellSlot !== null) {
        const slot = pendingSpellSlot;
        const spellId = hotbar.getSpellId(slot);
        const spell = spellId ? getSpellTemplate(spellId) : null;
        exitSpellMode();
        if (!spell) return;
        const hasDamage = spell.effects.some((e) => e.kind === "damage");
        const hasHealOrBuff = spell.effects.some(
          (e) => e.kind === "heal" || e.kind === "buffDmg" || e.kind === "absorb",
        );
        if (hasDamage) {
          // Mob target path — damage spells phase through allied players and
          // home onto the locked mob (body-block handled server-side).
          if (!mobOnTile) return;
          const liveMob = mobsById.get(mobOnTile.id);
          if (!liveMob || liveMob.state === "dead") return;
          worldView.setTargetMob(liveMob.id);
          client.send({ t: "castSpell", d: { slot, targetMobId: liveMob.id } });
          worldView.triggerLocalCast({ x: target.tx, y: target.ty });
          return;
        }
        if (hasHealOrBuff) {
          // Ally target path — clicking yourself routes to self-cast.
          const clickedPlayerId = playersByTile.get(tileKey(target.tx, target.ty));
          if (!clickedPlayerId) return; // click on ground / mob = cancel
          client.send({
            t: "castSpell",
            d: { slot, targetCharacterId: clickedPlayerId },
          });
          worldView.triggerLocalCast({ x: target.tx, y: target.ty });
          return;
        }
        if (spell.targeting === "aoeGround") {
          // Ground-targeted AOE — server validates the tile (walkable + in
          // range). Click on a mob still works (its tile is walkable from
          // the perspective of the AOE drop), so AoE can land on stacked
          // mobs without special handling.
          if (!isWalkable(tilemap, target.tx, target.ty)) return;
          client.send({
            t: "castSpell",
            d: { slot, targetTile: { x: target.tx, y: target.ty } },
          });
          worldView.triggerLocalCast({ x: target.tx, y: target.ty });
          return;
        }
        return;
      }
      if (mobOnTile) {
        const liveMob = mobsById.get(mobOnTile.id);
        if (!liveMob || liveMob.state === "dead") return;
        currentTargetMobId = liveMob.id;
        worldView.setTargetMob(liveMob.id);
        client.send({ t: "attack", d: { mobId: liveMob.id } });
        startApproachPath(liveMob);
        return;
      }
      const bagId = bagsByTile.get(tileKey(target.tx, target.ty));
      if (bagId) {
        const bag = bagsById.get(bagId);
        if (!bag) return;
        const cur = worldView.getPredicted().pos;
        const dist = Math.max(Math.abs(cur.x - bag.x), Math.abs(cur.y - bag.y));
        pendingBagOpen = bagId;
        if (dist <= 1) {
          client.send({ t: "openLoot", d: { bagId } });
          pendingBagOpen = null;
        } else {
          startApproachPath(bag);
          // openLoot will fire from maybeOpenPendingBag once we're adjacent.
        }
        return;
      }
      // M5.1 — NPC click. Same auto-walk-then-interact pattern as bags.
      const npcId = npcsByTile.get(tileKey(target.tx, target.ty));
      if (npcId) {
        const npc = npcsById.get(npcId);
        if (!npc) return;
        // Clicking an NPC drops the melee target — you're talking, not fighting.
        if (currentTargetMobId !== null) {
          currentTargetMobId = null;
          worldView.setTargetMob(null);
          client.send({ t: "cancelTarget" });
        }
        const cur = worldView.getPredicted().pos;
        const dist = Math.max(Math.abs(cur.x - npc.x), Math.abs(cur.y - npc.y));
        pendingNpcOpen = npcId;
        if (dist <= 1) {
          fireNpcInteract(npcId);
          pendingNpcOpen = null;
        } else {
          startApproachPath(npc);
          // fireNpcInteract will run from maybeOpenPendingVendor once adjacent.
        }
        return;
      }
      if (isWalkable(tilemap, target.tx, target.ty)) {
        // Click-to-walk on empty ground = "go there, stop fighting". Drop
        // the melee target locally (kills the auto-chase that would
        // otherwise drag the player back to the mob once the path empties)
        // and tell the server too. The cast stance is already cancelled
        // server-side by the move intent.
        if (currentTargetMobId !== null) {
          currentTargetMobId = null;
          worldView.setTargetMob(null);
          client.send({ t: "cancelTarget" });
        }
        startClickPath(target.tx, target.ty);
      }
    },
  });

  function pushPathAsQueue(path: Position[], from: Position): void {
    clickPath = [];
    let prev = from;
    for (const tile of path) {
      clickPath.push({
        dx: Math.sign(tile.x - prev.x) as -1 | 0 | 1,
        dy: Math.sign(tile.y - prev.y) as -1 | 0 | 1,
      });
      prev = tile;
    }
  }

  function startClickPath(tx: number, ty: number): void {
    if (!tilemap) return;
    const cur = worldView.getPredicted().pos;
    const path = findPath(tilemap, cur, { x: tx, y: ty });
    if (!path || path.length === 0) {
      clickPath = [];
      return;
    }
    worldView.flashHighlight(tx, ty);
    pushPathAsQueue(path, cur);
  }

  // Walk to a tile adjacent to the mob so the server's auto-attack triggers
  // once we're in melee range. clickPath steps carry keepTarget:true so the
  // approach doesn't drop the mob target picked above.
  function startApproachPath(mob: { x: number; y: number }): void {
    if (!tilemap) return;
    const cur = worldView.getPredicted().pos;
    const dist = Math.max(Math.abs(cur.x - mob.x), Math.abs(cur.y - mob.y));
    if (dist <= 1) {
      clickPath = [];
      return;
    }
    const path = findPath(tilemap, cur, { x: mob.x, y: mob.y });
    if (!path || path.length <= 1) {
      clickPath = [];
      return;
    }
    // Drop the final step (the mob's own tile) — we stop on the adjacent one.
    pushPathAsQueue(path.slice(0, -1), cur);
  }

  let bubbles: BubbleManager | null = null;
  let damageNumbers: DamageNumberManager | null = null;
  let xpFloater: XpFloater | null = null;
  let goldFloater: GoldFloater | null = null;
  void worldView.init().then(() => {
    const layer = worldView.bubbleLayer();
    if (layer) {
      bubbles = new BubbleManager(layer, worldView);
      damageNumbers = new DamageNumberManager(layer, worldView);
      xpFloater = new XpFloater(layer, worldView);
      goldFloater = new GoldFloater(layer, worldView);
    }
  });

  const chatPanel = new ChatPanel({
    onSendChat: (channel, text) => client.send({ t: "chat", d: { channel, text } }),
    onWho: () => client.send({ t: "who" }),
    onPartyInvite: (targetCharacterName) =>
      client.send({ t: "partyInvite", d: { targetCharacterName } }),
    onPartyLeave: () => client.send({ t: "partyLeave" }),
    setInputBlocked: (blocked) => {
      chatBlocked = blocked;
    },
  });
  worldMount.append(chatPanel.root);

  const keyboard = new KeyboardController({
    isInputBlocked: () => chatBlocked || isDead,
    onMoveTick: (dx, dy, source) => {
      if (!tilemap || isDead) return null;
      // Any keyboard or pointer-hold input cancels a queued click-path AND
      // exits spell-mode (T4C-style: moving aborts spell selection).
      if (source !== "queue") {
        clickPath = [];
        exitSpellMode();
      }
      const cur = worldView.getPredicted();
      const resolved = resolveMove(tilemap, cur.pos, dx, dy);
      if (!resolved) {
        if (source === "queue") clickPath = [];
        return null;
      }
      const slid = resolved.dx !== dx || resolved.dy !== dy;
      const facing = facingFromDelta(resolved.dx, resolved.dy, cur.facing);
      // Single setPredicted call: the underlying setLocalPosition handles
      // "mid-lerp chaining" via its else-branch (lerps from current
      // interpolated position to the new target with duration scaled by
      // the real distance). A prior `setPredicted(cur.pos, ..., true)`
      // snap call here was responsible for visible micro-jolts whenever
      // the previous lerp was still in flight when a new move fired.
      worldView.setPredicted({ x: cur.pos.x + resolved.dx, y: cur.pos.y + resolved.dy }, facing);
      lastMoveAt = performance.now();
      const keepTarget = source === "queue";
      const payload: { dx: -1 | 0 | 1; dy: -1 | 0 | 1; keepTarget?: boolean } = {
        dx: resolved.dx,
        dy: resolved.dy,
      };
      if (keepTarget) payload.keepTarget = true;
      client.send({ t: "move", d: payload });
      if (source === "queue") {
        clickPath.shift();
        // A slide diverges from the precomputed path — abandon the rest.
        if (slid) clickPath = [];
      } else if (currentTargetMobId !== null) {
        currentTargetMobId = null;
        worldView.setTargetMob(null);
      }
      // Pace the next tick on the *resolved* move so a slid diagonal (now a
      // cardinal) doesn't gate the keyboard for the longer diagonal cooldown.
      // Weight no longer affects movement, so the base cadence is final.
      return resolved.dx !== 0 && resolved.dy !== 0 ? MOVE_COOLDOWN_MS_DIAG : MOVE_COOLDOWN_MS;
    },
    getFallbackDirection: () => {
      // Priority 1: pointer hold (push-to-walk).
      if (pointerHeld && pointerTargetTile && !isDead) {
        if (!pointerInFollowMode && performance.now() - pointerStartedAt >= HOLD_TO_FOLLOW_MS) {
          pointerInFollowMode = true;
        }
        if (pointerInFollowMode) {
          const cur = worldView.getPredicted();
          const dx = Math.sign(pointerTargetTile.tx - cur.pos.x) as -1 | 0 | 1;
          const dy = Math.sign(pointerTargetTile.ty - cur.pos.y) as -1 | 0 | 1;
          if (dx !== 0 || dy !== 0) {
            // Pointer hold ALSO cancels a queued click-path.
            clickPath = [];
            return { dx, dy, source: "pointer" };
          }
        }
      }
      // Priority 2: queued click-to-move steps.
      if (clickPath.length > 0 && !isDead) {
        const next = clickPath[0]!;
        return { dx: next.dx, dy: next.dy, source: "queue" };
      }
      return null;
    },
  });
  keyboard.start();

  // Skip global hotkey handling when the user is typing in a form field
  // (vendor qty input, chat input, future panels). Without this, "1"–"8"
  // trigger spell-mode via preventDefault before the input sees the key, and
  // "i"/"k" toggle inventory/spellbook from inside an input.
  const isTypingInField = (e: KeyboardEvent): boolean => {
    const t = e.target as HTMLElement | null;
    if (!t) return false;
    const tag = t.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable;
  };

  const handleDebugKey = (e: KeyboardEvent): void => {
    if (chatBlocked || isTypingInField(e)) return;
    if (e.key === "F2") {
      e.preventDefault();
      worldView.setAggroOverlayVisible(!worldView.isAggroOverlayVisible());
      return;
    }
    if (e.key === "F3") {
      e.preventDefault();
      const next = !worldView.isMobDebugVisible();
      worldView.setMobDebugVisible(next);
      // Tell the server to start/stop streaming the per-mob debug
      // sidecar (losMissCount / swingAt / targetCharacterId). Off by
      // default → the regular worldSnapshot stays lean for non-debug
      // players.
      client.send({ t: "setMobDebug", d: { visible: next } });
      toast.show(next ? "Mob debug ON (F3)" : "Mob debug OFF");
      return;
    }
  };
  window.addEventListener("keydown", handleDebugKey);

  const handleInventoryKey = (e: KeyboardEvent): void => {
    if (e.key !== "i" && e.key !== "I") return;
    if (chatBlocked || isTypingInField(e)) return;
    e.preventDefault();
    inventoryPanel.toggle();
  };
  window.addEventListener("keydown", handleInventoryKey);

  const handleSpellbookKey = (e: KeyboardEvent): void => {
    if (e.key !== "k" && e.key !== "K") return;
    if (chatBlocked || isTypingInField(e)) return;
    e.preventDefault();
    spellbookPanel.toggle();
  };
  window.addEventListener("keydown", handleSpellbookKey);

  // M17.4 — Colossus Leap on "G" (gated client-side on the don being unlocked
  // to avoid spamming the server; the server re-validates anyway).
  const handleLeapKey = (e: KeyboardEvent): void => {
    if (e.key !== "g" && e.key !== "G") return;
    if (chatBlocked || isTypingInField(e)) return;
    if (!lastUnlockedPassives || !lastUnlockedPassives.has("colossus_leap")) return;
    e.preventDefault();
    client.send({ t: "useDon", d: { donId: "colossus_leap" } });
  };
  window.addEventListener("keydown", handleLeapKey);

  // Hotbar keys 1-8 → enter T4C-style spell-mode for that slot. The cursor
  // turns crosshair; next mob click sends the cast with explicit target so
  // the player doesn't auto-walk to melee. Pressing the same key again
  // exits the mode.
  const handleHotbarKey = (e: KeyboardEvent): void => {
    if (chatBlocked || isTypingInField(e)) return;
    if (e.repeat) return;
    // ESC priority order: (1) exit pre-cast spell-mode if active, (2) cancel
    // an active auto-cast stance. Both can be true (spell-mode reentered
    // mid-loop), so we handle them in sequence.
    if (e.key === "Escape") {
      let handled = false;
      if (pendingSpellSlot !== null) {
        exitSpellMode();
        handled = true;
      }
      if (castActive) {
        client.send({ t: "cancelCast" });
        handled = true;
      }
      if (handled) e.preventDefault();
      return;
    }
    const n = Number(e.key);
    if (!Number.isInteger(n) || n < 1 || n > 8) return;
    e.preventDefault();
    const slot = n - 1;
    const spellId = hotbar.getSpellId(slot);
    if (!spellId) return; // empty slot
    const spell = getSpellTemplate(spellId);
    // Self-only spells (Arcane Shield) skip spell-mode entirely — there's
    // no target to pick, fire on press regardless of modifiers.
    if (spell?.targeting === "self") {
      client.send({ t: "castSpell", d: { slot } });
      worldView.triggerLocalCast(); // self-cast: anim plays, no rotation
      return;
    }
    // Alt+N → self-cast (LoL-style). Skips spell-mode and tells the server
    // to target the caster directly. Only meaningful for ally-target spells
    // (heals / buffs); damage spells will reject server-side (target_dead
    // or similar since caster isn't a mob).
    if (e.altKey) {
      if (!selfId) return;
      client.send({ t: "castSpell", d: { slot, targetCharacterId: selfId } });
      worldView.triggerLocalCast(); // self-cast: anim plays, no rotation
      return;
    }
    enterSpellMode(slot);
  };
  window.addEventListener("keydown", handleHotbarKey);

  function update(s: AppState): void {
    if (s.screen.kind !== "in_game") return;
    const players = s.screen.online;
    presenceText.textContent = `${players.length} online: `;
    onlineNames.textContent = players.map((p) => p.name).join(", ") || "—";
  }

  function onZoneEnter(
    meta: ZoneEnterMessage,
    selfCharacterId: string,
    pos: Position,
    facing: Facing,
  ): void {
    // M11 — synthesize the local TilemapJson with placeholder arrays
    // (collision defaults to true so unexplored tiles block movement;
    // background defaults to grass for the rare case the renderer reads
    // it before any chunk patches it). Chunks fill in real values via
    // onChunkData. Memory: 1024² zone = 1M cells = ~16 MB array,
    // tolerable on the client.
    const cellCount = meta.width * meta.height;
    const background: TerrainId[] = new Array(cellCount).fill("grass" as TerrainId);
    const collision: boolean[] = new Array(cellCount).fill(true);
    tilemap = {
      zoneId: meta.zoneId,
      width: meta.width,
      height: meta.height,
      tileWidth: TILE_W,
      tileHeight: TILE_H,
      isSafe: meta.isSafe,
      background,
      collision,
      spawnPoints: meta.spawnPoints,
      portals: meta.portals,
    };
    selfId = selfCharacterId;
    character.zoneId = meta.zoneId;
    character.position = { ...pos };
    character.facing = facing;
    mobsByTile.clear();
    mobsById.clear();
    playersByTile.clear();
    bagsByTile.clear();
    bagsById.clear();
    npcsByTile.clear();
    npcsById.clear();
    if (openBagId) lootWindow.close();
    if (vendorWindow.isVisible()) vendorWindow.forceClose();
    if (questWindow.isVisible()) questWindow.forceClose();
    pendingNpcOpen = null;
    currentTargetMobId = null;
    clickPath = [];
    sheet.setZone(meta.zoneId, pos);
    worldView.setZone(
      { zoneId: meta.zoneId, width: meta.width, height: meta.height, isSafe: meta.isSafe },
      selfCharacterId,
      character.name,
      pos,
      facing,
    );
  }

  function onChunkData(chunk: ChunkData & { zoneId: string }): void {
    // Drop chunks for a zone we've already left (race window between a
    // teleport and the in-flight chunk push for the previous zone).
    if (!tilemap || tilemap.zoneId !== chunk.zoneId) return;
    // Patch the synthetic tilemap arrays so isWalkable/findPath/
    // resolveMove see real terrain in this region.
    const x0 = chunk.cx * CHUNK_SIZE;
    const y0 = chunk.cy * CHUNK_SIZE;
    for (let dy = 0; dy < chunk.height; dy++) {
      for (let dx = 0; dx < chunk.width; dx++) {
        const wx = x0 + dx;
        const wy = y0 + dy;
        const ix = wy * tilemap.width + wx;
        tilemap.background[ix] = chunk.terrain[dy * chunk.width + dx]!;
        tilemap.collision[ix] = chunk.collision[dy * chunk.width + dx]!;
      }
    }
    // Forward to the renderer so the chunk's tiles become visible.
    worldView.ingestChunk(chunk);
  }

  function onSnapshot(snap: {
    players: PlayerSnapshot[];
    mobs: MobSnapshot[];
    projectiles: ProjectileSnapshot[];
    serverTime: number;
  }): void {
    if (!selfId) return;
    worldView.ingestSnapshot(snap.players);
    worldView.ingestMobSnapshot(snap.mobs, snap.serverTime);
    worldView.ingestProjectiles(snap.projectiles);
    castBar.tick(snap.serverTime);
    // M8.x — feed party panel HP/mana bars. The panel filters by
    // membership; everyone else is ignored silently.
    partyPanel.setMemberStats(
      snap.players.map((p) => ({
        characterId: p.id,
        hpPct: p.hpPct,
        manaPct: p.manaPct,
      })),
    );

    mobsByTile.clear();
    mobsById.clear();
    playersByTile.clear();
    for (const m of snap.mobs) {
      mobsById.set(m.id, m);
      if (m.state !== "dead") mobsByTile.set(tileKey(m.x, m.y), { id: m.id, templateId: m.templateId });
    }
    for (const pl of snap.players) {
      if (pl.lifeState !== "alive") continue;
      // Two players on the same tile is allowed (players don't collide). Last
      // one wins for the spell-mode click hit-test — minor edge case in
      // PvE-only MVP, ally-targeting via UI portrait will land in a later
      // slice if it matters.
      playersByTile.set(tileKey(pl.x, pl.y), pl.id);
    }
    // Use the predicted position for self so clicking the visible sprite
    // always resolves to self even when the server snapshot lags behind
    // local prediction.
    if (selfId && !isDead) {
      const pred = worldView.getPredicted();
      playersByTile.set(tileKey(pred.pos.x, pred.pos.y), selfId);
    }
    if (currentTargetMobId) {
      const t = mobsById.get(currentTargetMobId);
      if (!t || t.state === "dead") {
        currentTargetMobId = null;
        worldView.setTargetMob(null);
      }
    }

    const localServer = snap.players.find((p) => p.id === selfId);
    if (!localServer) return;
    const pred = worldView.getPredicted();
    const dx = localServer.x - pred.pos.x;
    const dy = localServer.y - pred.pos.y;
    const tileDiff = Math.abs(dx) + Math.abs(dy);
    sheet.setZone(character.zoneId, { x: localServer.x, y: localServer.y });
    if (tileDiff === 0) {
      if (pred.facing !== localServer.facing) {
        worldView.setPredicted(pred.pos, localServer.facing);
      }
    } else {
      // Movement is fully predictive on the client. A nonzero diff means
      // one of three things:
      //   (a) we're inside the prediction round-trip window (withinGrace)
      //       — silently let the visual lerp catch up, no action needed
      //   (b) soft drift outside the grace window (1–2 tiles off, server
      //       confirms slightly stale state due to 20Hz tick lag) — update
      //       the authoritative predictedX/Y so subsequent moves chain
      //       from the corrected origin, and smoothly lerp the visual to
      //       it (no snap). The autopilot path keeps running from the
      //       corrected pos, so cosmetic catchup is invisible.
      //   (c) hard desync (≥ HARD_DESYNC_TILES) — snap immediately and
      //       cancel the queued path; lerping over multiple tiles would
      //       look weirder than a sharp correction.
      const now = performance.now();
      const withinGrace = now - lastMoveAt <= RECONCILE_GRACE_MS;
      if (tileDiff >= HARD_DESYNC_TILES) {
        worldView.setPredicted({ x: localServer.x, y: localServer.y }, localServer.facing, true);
        clickPath = [];
      } else if (!withinGrace) {
        // Soft correction — lerp, don't snap. predictedX/Y is updated by
        // setPredicted regardless of the snap flag, so logic stays consistent.
        worldView.setPredicted({ x: localServer.x, y: localServer.y }, localServer.facing, false);
      }
    }

    maybeChaseTarget();
    maybeOpenPendingBag();
    maybeOpenPendingVendor();
  }

  function maybeOpenPendingBag(): void {
    if (!pendingBagOpen) return;
    const bag = bagsById.get(pendingBagOpen);
    if (!bag) {
      pendingBagOpen = null;
      return;
    }
    const cur = worldView.getPredicted().pos;
    const dist = Math.max(Math.abs(cur.x - bag.x), Math.abs(cur.y - bag.y));
    if (dist <= 1) {
      client.send({ t: "openLoot", d: { bagId: pendingBagOpen } });
      pendingBagOpen = null;
    }
  }

  function maybeOpenPendingVendor(): void {
    if (!pendingNpcOpen) return;
    const npc = npcsById.get(pendingNpcOpen);
    if (!npc) {
      pendingNpcOpen = null;
      return;
    }
    const cur = worldView.getPredicted().pos;
    const dist = Math.max(Math.abs(cur.x - npc.x), Math.abs(cur.y - npc.y));
    if (dist <= 1) {
      fireNpcInteract(pendingNpcOpen);
      pendingNpcOpen = null;
    }
  }

  // Dispatch NPC interaction based on the template's declared capabilities.
  // Vendor → vendorOpen (vendor modal). Quest-giver → interactNpc, server
  // replies with npcDialog that opens the quest modal.
  function fireNpcInteract(npcId: string): void {
    const npc = npcsById.get(npcId);
    if (!npc) return;
    const template = getNpcTemplate(npc.templateId);
    if (!template) return;
    if (template.vendor) {
      client.send({ t: "vendorOpen", d: { npcId } });
      return;
    }
    if (template.quests) {
      client.send({ t: "interactNpc", d: { npcId } });
      return;
    }
  }

  // Auto-chase: while a clicked mob is alive and out of melee range, refresh
  // the approach path each snapshot so the player tracks it. Stops chasing
  // (drops the target client + server) once the mob escapes past MAX_CHASE_TILES.
  function maybeChaseTarget(): void {
    if (!currentTargetMobId || isDead || clickPath.length > 0) return;
    const target = mobsById.get(currentTargetMobId);
    if (!target || target.state === "dead") return;
    const cur = worldView.getPredicted().pos;
    const dist = Math.max(Math.abs(cur.x - target.x), Math.abs(cur.y - target.y));
    if (dist <= 1) return;
    if (dist > MAX_CHASE_TILES) {
      currentTargetMobId = null;
      worldView.setTargetMob(null);
      client.send({ t: "cancelTarget" });
      return;
    }
    startApproachPath(target);
  }

  function onChatPosted(msg: ChatMessage): void {
    chatPanel.appendServer(msg);
    if (msg.channel === "local" && msg.senderCharacterId) {
      bubbles?.spawn(msg.senderCharacterId, msg.text);
    }
  }

  function onMoveRejected(serverX: number, serverY: number): void {
    const cur = worldView.getPredicted();
    worldView.setPredicted({ x: serverX, y: serverY }, cur.facing, true);
    clickPath = [];
  }

  function onCombatHit(p: CombatHitPayload): void {
    // Local player is the attacker on this hit → fire the appropriate attack
    // one-shot so the visible swing lines up with the server-confirmed damage.
    // Only on MELEE — spell projectiles and AOE ticks also land here but
    // shouldn't make the character swing. Weapon in RightHand decides which
    // anim plays: bare hands → punching, 1H → attack_1h, 2H → attack_2h.
    // targetId pivots the character to face the actual target tile.
    if (p.attackerId === selfId && p.attackerKind === "player" && p.source === "melee") {
      const rightHand = equippedCache.RightHand;
      const template = rightHand ? getItemTemplate(rightHand.templateId) : undefined;
      const slot = template?.slot;
      const isTwoHanded = Array.isArray(slot) && slot.length >= 2;
      if (isTwoHanded) {
        worldView.triggerLocalAttack2h(p.targetId);
      } else if (template) {
        worldView.triggerLocalAttack1h(p.targetId);
      } else {
        worldView.triggerLocalPunch(p.targetId);
      }
    }
    if (!damageNumbers) return;
    const variant: "incoming" | "outgoing" | "self" =
      p.targetId === selfId ? "incoming" : p.attackerId === selfId ? "outgoing" : "self";
    damageNumbers.spawn({
      amount: p.damage,
      crit: p.crit,
      anchorId: p.targetId,
      variant,
    });
  }

  function onCombatHeal(p: CombatHealPayload): void {
    // Local player healed someone (or themselves) → cast anim. No rotation
    // for now: ally tile lookup would require a remote-player position map
    // we don't surface here. Self-heals naturally don't rotate anyway.
    if (p.casterId === selfId) {
      worldView.triggerLocalCast();
    }
    if (!damageNumbers) return;
    damageNumbers.spawn({
      amount: p.amount,
      crit: false,
      anchorId: p.targetCharacterId,
      variant: "heal",
    });
  }

  function onEntityDied(p: EntityDiedPayload): void {
    if (p.kind === "mob" && currentTargetMobId === p.id) {
      currentTargetMobId = null;
      worldView.setTargetMob(null);
    }
  }

  function onEntityRespawned(_p: EntityRespawnedPayload): void {
    /* visual flair handled elsewhere; nothing to do for now */
  }

  function onSelfStats(p: SelfStatsPayload): void {
    currentWeight = p.currentWeight;
    maxWeight = p.maxWeight;
    copperCache = p.copper;
    // M17.4 — fire a golden toast for any newly-unlocked don. On the FIRST
    // selfStats we seed the set silently (no toast spam for dons already
    // unlocked at login).
    if (lastUnlockedPassives === null) {
      lastUnlockedPassives = new Set(p.unlockedPassives);
    } else {
      for (const id of p.unlockedPassives) {
        if (!lastUnlockedPassives.has(id)) {
          const tpl = getPassiveTemplate(id);
          toast.show(`✦ Don débloqué : ${tpl?.name ?? id}`);
        }
      }
      lastUnlockedPassives = new Set(p.unlockedPassives);
    }
    // M17.4 — Scholar's Eye toggles the exact level+HP labels on mobs.
    worldView.setScholarsEye(p.unlockedPassives.includes("scholars_eye"));
    sheet.setStats(p);
    if (vendorWindow.isVisible()) {
      vendorWindow.setSelfStats(p.copper, p.currentWeight, p.maxWeight);
    }
    // M8.x — keep the party panel's self-row in sync without waiting for
    // the next worldSnapshot tick (selfStats fires on every regen, mana
    // spend, damage taken).
    if (selfId) {
      partyPanel.setMemberStats([
        {
          characterId: selfId,
          hpPct: p.maxHp > 0 ? p.hp / p.maxHp : 0,
          manaPct: p.maxMana > 0 ? p.mana / p.maxMana : 0,
        },
      ]);
    }
    if (p.lifeState === "dead") {
      isDead = true;
      currentTargetMobId = null;
      worldView.setTargetMob(null);
      deathOverlay.show(p.respawnAt ?? Date.now() + 5000);
    } else {
      isDead = false;
      deathOverlay.hide();
    }
  }

  // M17.4 — an active don fired server-side: snap the predicted local position
  // to the authoritative tile (reconciles client prediction with the leap).
  function onDonUsed(d: { donId: string; x: number; y: number }): void {
    const { facing } = worldView.getPredicted();
    worldView.setPredicted({ x: d.x, y: d.y }, facing, true);
    toast.show("✦ Colossus Leap");
  }

  function onXpGained(p: XpGainedPayload): void {
    if (!selfId) return;
    xpFloater?.spawn({ amount: p.amount, anchorId: selfId });
  }

  function onGoldGained(p: GoldGainedPayload): void {
    if (!selfId) return;
    goldFloater?.spawn({ amount: p.amount, anchorId: selfId });
  }

  function onLevelUp(p: LevelUpPayload): void {
    levelUpBanner.show(p.newLevel);
  }

  // Maps an equipped item template → its rendered sprite id, per visual slot.
  // Extend as more sprites are rendered; unmapped items show no layer.
  const HEAD_ITEM_TO_HELMET_SPRITE: Record<string, string> = {
    leather_cap: "leather_helm",
  };
  const RIGHTHAND_ITEM_TO_WEAPON_SPRITE: Record<string, string> = {
    iron_sword: "sword_iron",
    iron_battleaxe: "axe_iron",
  };
  const LEFTHAND_ITEM_TO_SHIELD_SPRITE: Record<string, string> = {
    wooden_shield: "shield_wood",
  };
  // Torso armour is a LAYER with per-pixel body occlusion baked in (the Meshy
  // vest rendered with the body), so it composites like the others.
  const CHEST_ITEM_TO_ARMOR_SPRITE: Record<string, string> = {
    leather_tunic: "leather_chest",
  };
  // Leg armour — same skinned + per-pixel-occluded LAYER as the torso.
  const LEGS_ITEM_TO_ARMOR_SPRITE: Record<string, string> = {
    leather_pants: "leather_pants",
  };

  // Sync the local player's equipment layers to whatever is equipped. null
  // (empty slot or unmapped item) hides that layer. Safe before the sprite
  // controller has loaded — the request is deferred internally.
  function syncLocalEquipment(): void {
    const head = equippedCache.Head;
    worldView.setLocalLayer(
      "helmet",
      head ? (HEAD_ITEM_TO_HELMET_SPRITE[head.templateId] ?? null) : null,
    );
    const rightHand = equippedCache.RightHand;
    worldView.setLocalLayer(
      "main_hand",
      rightHand ? (RIGHTHAND_ITEM_TO_WEAPON_SPRITE[rightHand.templateId] ?? null) : null,
    );
    const leftHand = equippedCache.LeftHand;
    worldView.setLocalLayer(
      "off_hand",
      leftHand ? (LEFTHAND_ITEM_TO_SHIELD_SPRITE[leftHand.templateId] ?? null) : null,
    );
    const chest = equippedCache.Chest;
    worldView.setLocalLayer(
      "chest",
      chest ? (CHEST_ITEM_TO_ARMOR_SPRITE[chest.templateId] ?? null) : null,
    );
    const legs = equippedCache.Legs;
    worldView.setLocalLayer(
      "legs",
      legs ? (LEGS_ITEM_TO_ARMOR_SPRITE[legs.templateId] ?? null) : null,
    );
  }

  function onInventoryUpdate(p: InventoryUpdatePayload): void {
    inventoryCache = p.inventory;
    equippedCache = p.equipped;
    inventoryPanel.setData(p.inventory, p.equipped);
    if (vendorWindow.isVisible()) vendorWindow.setInventory(p.inventory);
    syncLocalEquipment();
  }

  function refreshAfterCatalogueUpdate(): void {
    // M7.1 — admin published a new draft (or the initial post-zoneEnter
    // snapshot landed). Re-feed the inventory panel so tooltips read from
    // the freshly-swapped registry. Vendor window similarly if open.
    inventoryPanel.setData(inventoryCache, equippedCache);
    if (vendorWindow.isVisible()) vendorWindow.setInventory(inventoryCache);
  }

  function onEquipError(reason: string): void {
    toast.show(humanizeEquipError(reason));
  }

  function onHotbarUpdate(p: HotbarUpdatePayload): void {
    hotbar.setSlots(p.hotbar);
    spellbookPanel.setKnown(p.knownSpellIds);
  }

  function onCastStateUpdate(p: CastStateUpdatePayload): void {
    castBar.setState(p.spellId, p.castEndsAt, p.castDurationMs);
    castActive = p.spellId !== null;
    // Spell-only targets don't set currentTargetMobId (to avoid auto-chase),
    // so the snapshot-driven ring cleanup can't catch them. When the cast
    // stance ends with no melee target active, clear the visual ring here.
    if (p.spellId === null && currentTargetMobId === null) {
      worldView.setTargetMob(null);
    }
  }

  function onCastError(reason: string): void {
    toast.show(humanizeCastError(reason));
  }

  function onBuffUpdate(p: BuffUpdatePayload): void {
    // Tracker chip only the local player; the sprite glow renders for any
    // player so allies see buffs on each other.
    if (p.playerId === selfId) buffTracker.setBuffs(p.buffs);
    worldView.setPlayerBuffGlow(p.playerId, pickBuffGlowColor(p.buffs));
  }

  function onGroundBagsUpdate(p: GroundBagsUpdatePayload): void {
    if (p.zoneId !== character.zoneId) return;
    bagsByTile.clear();
    bagsById.clear();
    for (const b of p.bags) {
      bagsByTile.set(tileKey(b.x, b.y), b.bagId);
      bagsById.set(b.bagId, { x: b.x, y: b.y, itemCount: b.itemCount });
    }
    worldView.ingestBags(p.bags);
    if (openBagId && !bagsById.has(openBagId)) {
      lootWindow.closeIfBag(openBagId);
      openBagId = null;
    }
  }

  function onLootContents(p: LootContentsPayload): void {
    lootWindow.open(p.bagId, p.items);
    openBagId = p.bagId;
  }

  function onProjectileSpawned(p: ProjectileSpawnedPayload): void {
    worldView.spawnProjectile(p);
    // Damage spells auto-loop server-side until target dies or player moves.
    // Each loop iteration fires a fresh projectileSpawned → re-trigger the
    // cast anim so the visual matches every shot, not just the first click.
    // Rotate toward the active melee target if we have one (currentTargetMobId
    // tracks it across the loop); otherwise keep last facing.
    if (p.attackerId === selfId && p.attackerKind === "player") {
      const targetMob = currentTargetMobId ? mobsById.get(currentTargetMobId) : null;
      worldView.triggerLocalCast(
        targetMob ? { x: targetMob.x, y: targetMob.y } : undefined,
      );
    }
  }

  function onProjectileExpired(p: ProjectileExpiredPayload): void {
    worldView.expireProjectile(p);
  }

  function onAreaEffectSpawned(p: AreaEffectSpawnedPayload): void {
    worldView.spawnAreaEffect(p);
    // AOE spells (Rain of Fire) are one-shot ground placements, no loop —
    // but mirror the projectile path for consistency: caster anim + rotation
    // toward the landing tile.
    if (p.casterId === selfId) {
      worldView.triggerLocalCast({ x: p.x, y: p.y });
    }
  }

  function onAreaEffectExpired(p: AreaEffectExpiredPayload): void {
    worldView.expireAreaEffect(p.areaId);
  }

  function onNpcsInZone(p: NpcsInZonePayload): void {
    if (p.zoneId !== character.zoneId) return;
    npcsByTile.clear();
    npcsById.clear();
    for (const n of p.npcs) {
      npcsByTile.set(tileKey(n.x, n.y), n.npcId);
      npcsById.set(n.npcId, { x: n.x, y: n.y, templateId: n.templateId });
    }
    worldView.ingestNpcs(p.npcs);
  }

  function onVendorPanel(p: VendorPanelPayload): void {
    const template = getNpcTemplate(p.templateId);
    if (!template) {
      toast.show("Vendor is unavailable.");
      return;
    }
    // Only one NPC modal at a time — close any open quest dialog.
    if (questWindow.isVisible()) questWindow.forceClose();
    openVendorNpcId = p.npcId;
    vendorWindow.open({
      npcId: p.npcId,
      vendorName: template.name,
      dialogText: template.dialogText,
      stock: p.stock,
      sellMultiplier: p.sellMultiplier,
      inventory: inventoryCache,
      copper: copperCache,
      currentWeight,
      maxWeight,
    });
  }

  function onVendorResult(_p: VendorResultPayload): void {
    // Just close the modal — the farewell bubble itself arrives via the
    // server's zone-broadcast `npcSpeech` so nearby players see it too.
    // close() resets the cart maps for a clean re-open.
    vendorWindow.close();
  }

  function onNpcSpeech(p: NpcSpeechPayload): void {
    worldView.showNpcSpeech(p.npcId, p.text, p.durationMs);
  }

  function onVendorError(code: VendorErrorCode): void {
    toast.show(humanizeVendorError(code));
    // Out-of-range / vendor-gone errors mean the open modal is stale; close
    // it so the user goes through the click flow again.
    if (code === "out_of_range" || code === "vendor_not_found") {
      vendorWindow.forceClose();
    }
  }

  function onNpcDialog(p: NpcDialogPayload): void {
    const template = getNpcTemplate(p.templateId);
    if (!template) {
      toast.show("Quest giver unavailable.");
      return;
    }
    // Only one NPC modal at a time — close any open vendor cart.
    if (vendorWindow.isVisible()) vendorWindow.forceClose();
    openQuestNpcId = p.npcId;
    questWindow.open({
      npcId: p.npcId,
      vendorName: template.name,
      dialogText: template.dialogText,
      available: p.available,
      active: p.active,
      completable: p.completable,
    });
  }

  function onQuestLogUpdate(p: QuestLogUpdatePayload): void {
    questWindow.syncFromLog(p.questLog);
    questTracker.setEntries(p.questLog);
  }

  function onQuestError(code: QuestErrorCode): void {
    toast.show(humanizeQuestError(code));
    if (code === "out_of_range" || code === "npc_not_found") {
      questWindow.forceClose();
    }
  }

  function onPartyUpdate(p: PartyUpdatePayload): void {
    partyPanel.setParty(p.party);
  }

  function onPartyInviteIncoming(p: PartyInviteIncomingPayload): void {
    inviteToast.show({
      fromCharacterName: p.fromCharacterName,
      expiresAt: p.expiresAt,
    });
  }

  function onPartyError(code: PartyErrorCode): void {
    toast.show(humanizePartyError(code));
  }

  function onRateLimited(retryAfterMs: number): void {
    const seconds = Math.max(1, Math.ceil(retryAfterMs / 1000));
    toast.show(`Slow down — try again in ${seconds}s.`);
  }

  function destroy(): void {
    keyboard.stop();
    window.removeEventListener("keydown", handleDebugKey);
    window.removeEventListener("keydown", handleInventoryKey);
    window.removeEventListener("keydown", handleSpellbookKey);
    window.removeEventListener("keydown", handleHotbarKey);
    chatPanel.destroy();
    bubbles?.destroy();
    damageNumbers?.destroy();
    xpFloater?.destroy();
    goldFloater?.destroy();
    deathOverlay.destroy();
    levelUpBanner.destroy();
    toast.destroy();
    inventoryPanel.destroy();
    spellbookPanel.destroy();
    hotbar.destroy();
    castBar.destroy();
    buffTracker.destroy();
    lootWindow.destroy();
    vendorWindow.forceClose();
    questWindow.forceClose();
    questTracker.destroy();
    partyPanel.destroy();
    inviteToast.destroy();
    worldView.destroy();
  }

  update(store.get());

  return {
    root,
    update,
    onZoneEnter,
    onChunkData,
    onSnapshot,
    onMobDebugSnapshot: (p) => worldView.applyMobDebugSnapshot(p.mobs),
    onChatPosted,
    onMoveRejected,
    onCombatHit,
    onCombatHeal,
    onEntityDied,
    onEntityRespawned,
    onSelfStats,
    onDonUsed,
    onXpGained,
    onGoldGained,
    onLevelUp,
    onInventoryUpdate,
    onEquipError,
    onHotbarUpdate,
    onCastStateUpdate,
    onCastError,
    onBuffUpdate,
    onGroundBagsUpdate,
    onLootContents,
    onProjectileSpawned,
    onProjectileExpired,
    onAreaEffectSpawned,
    onAreaEffectExpired,
    onNpcsInZone,
    onVendorPanel,
    onVendorResult,
    onVendorError,
    onNpcSpeech,
    onNpcDialog,
    onQuestLogUpdate,
    onQuestError,
    onPartyUpdate,
    onPartyInviteIncoming,
    onPartyError,
    onRateLimited,
    refreshAfterCatalogueUpdate,
    destroy,
  };
}

function humanizeEquipError(reason: string): string {
  switch (reason) {
    case "stat_required":
      return "Stat requirements not met.";
    case "not_equippable":
      return "Item cannot be equipped.";
    case "dead":
      return "Cannot equip while dead.";
    case "not_found":
    case "invalid_item":
      return "Item is no longer available.";
    default:
      return `Equip failed (${reason}).`;
  }
}

// Picks a single glow tint from the buff list. Absorb (shield) wins over
// damage buffs because shields communicate a survival state worth seeing at
// a glance; the tracker UI carries the precise list anyway.
function pickBuffGlowColor(buffs: BuffSnapshot[]): number | null {
  if (buffs.length === 0) return null;
  if (buffs.some((b) => b.kind === "absorb")) return 0x7fc8ff; // light blue
  if (buffs.some((b) => b.kind === "buffDmg")) return 0xd4af37; // gold
  return 0xc8a2ff; // generic fallback (arcane purple)
}

function humanizeVendorError(code: VendorErrorCode): string {
  switch (code) {
    case "vendor_not_found":
      return "Vendor is no longer here.";
    case "out_of_range":
      return "Too far from the vendor.";
    case "not_enough_copper":
      return "Not enough copper.";
    case "too_heavy":
      return "Too heavy to carry.";
    case "item_not_stocked":
      return "Item not in stock.";
    case "instance_not_owned":
      return "You no longer have that item.";
    case "instance_equipped":
      return "Unequip before selling.";
    case "quantity_invalid":
      return "Invalid quantity.";
  }
}

function humanizeQuestError(code: QuestErrorCode): string {
  switch (code) {
    case "npc_not_found":
      return "That person isn't here.";
    case "out_of_range":
      return "Too far away to speak.";
    case "quest_not_offered":
      return "They don't offer that quest.";
    case "already_active":
      return "You're already on that quest.";
    case "already_completed":
      return "You've already finished that quest.";
    case "not_active":
      return "You're not on that quest.";
    case "not_completable":
      return "That quest isn't ready to turn in.";
  }
}

function humanizePartyError(code: PartyErrorCode): string {
  switch (code) {
    case "target_not_found":
      return "No one by that name is online.";
    case "target_already_in_party":
      return "They're already in a party.";
    case "target_self":
      return "Can't invite yourself.";
    case "party_full":
      return "Party is full.";
    case "no_party":
      return "You're not in a party.";
    case "not_leader":
      return "Only the party leader can do that.";
    case "member_not_in_party":
      return "That player isn't in your party.";
    case "no_pending_invite":
      return "No pending invitation.";
    case "invite_expired":
      return "The invitation expired.";
  }
}

function humanizeCastError(reason: string): string {
  switch (reason) {
    case "no_target":
      return "No target.";
    case "target_dead":
      return "Target is dead.";
    case "out_of_range":
      return "Out of range.";
    case "no_mana":
      return "Not enough mana.";
    case "empty_slot":
      return "Empty hotbar slot.";
    case "spell_locked":
      return "Spell not unlocked.";
    case "dead":
      return "Cannot cast while dead.";
    case "not_implemented":
      return "This spell type isn't ready yet.";
    default:
      return `Cast failed (${reason}).`;
  }
}

function facingFromDelta(dx: number, dy: number, current: Facing): Facing {
  if (dx === 0 && dy === 0) return current;
  if (Math.abs(dx) >= Math.abs(dy)) return dx > 0 ? "E" : "W";
  return dy > 0 ? "S" : "N";
}

// Returns the actual (dx, dy) the player should walk this step, or null if
// fully blocked. For a diagonal intent that hits a wall (target tile or
// corner-cut), tries the two cardinal slides — first horizontal (dx, 0) then
// vertical (0, dy) — to glide along the obstacle instead of stopping.
function resolveMove(
  map: TilemapJson,
  from: Position,
  dx: -1 | 0 | 1,
  dy: -1 | 0 | 1,
): { dx: -1 | 0 | 1; dy: -1 | 0 | 1 } | null {
  if (dx === 0 && dy === 0) return null;
  if (canStep(map, from, dx, dy)) return { dx, dy };
  if (dx !== 0 && dy !== 0) {
    if (canStep(map, from, dx, 0)) return { dx, dy: 0 };
    if (canStep(map, from, 0, dy)) return { dx: 0, dy };
  }
  return null;
}

function canStep(map: TilemapJson, from: Position, dx: number, dy: number): boolean {
  if (dx === 0 && dy === 0) return false;
  const nx = from.x + dx;
  const ny = from.y + dy;
  if (!isWalkable(map, nx, ny)) return false;
  if (dx !== 0 && dy !== 0) {
    if (!isWalkable(map, from.x + dx, from.y)) return false;
    if (!isWalkable(map, from.x, from.y + dy)) return false;
  }
  return true;
}

interface SheetCallbacks {
  onAllocate: (stat: StatKey) => void;
}

interface SheetHandles {
  root: HTMLElement;
  setStats(p: SelfStatsPayload): void;
  setZone(zoneId: string, pos: Position): void;
}

// M17 — allocation projection (REFONTE-STATS): show the player the
// COMPOUNDING value of a point at the moment they spend it, turning the
// early-investment trap into an informed choice. Derived from the live game
// config so it stays correct when the admin retunes the knobs. Pools are
// wired now (M17.1); proc/regen effects join as later milestones land.
function statBankingHint(key: StatKey): string {
  const cfg = getGameConfig();
  const hpPrim = bankPts(11, 10) * cfg.hp.perStat; // +1 to the primary stat
  const hpSec = bankPts(10, 11) * cfg.hp.perStat; // +1 to the secondary stat
  const mpPrim = bankPts(11, 10) * cfg.mana.perStat;
  const mpSec = bankPts(10, 11) * cfg.mana.perStat;
  const n = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(2));
  switch (key) {
    case "con":
      return `+1 CON → +${n(hpPrim)} HP at every future level-up (compounds — invest early)`;
    case "str":
      return `+1 STR → +${n(hpSec)} HP per level-up (compounds) + live carry & melee`;
    case "int":
      return `+1 INT → +${n(mpPrim)} mana at every future level-up (compounds — invest early)`;
    case "fai":
      return `+1 FAI → +${n(mpSec)} mana per level-up (compounds) + mana regen`;
    case "dex":
      return `+1 DEX → live crit chance (banked procs come with combat update)`;
    case "rap":
      return `RAP does NOT compound — invest it anytime without losing value`;
  }
}

// M17.4 — render the Dons panel: unlocked dons (✦) + the closest locked one
// with its remaining requirement, so the player sees the next carrot.
function renderDons(
  container: HTMLElement,
  unlockedIds: string[],
  baseStats: CharacterStats,
): void {
  container.replaceChildren();
  const unlocked = new Set(unlockedIds);
  const all = getPassiveCatalogue();
  const reqStr = (d: { requirements: Partial<Record<string, number>> }): string =>
    Object.entries(d.requirements)
      .map(([s, v]) => `${s.toUpperCase()} ${v}`)
      .join(", ");
  // Closeness = total remaining stat points to reach the threshold(s).
  const gap = (d: { requirements: Partial<Record<string, number>> }): number => {
    let g = 0;
    for (const [s, v] of Object.entries(d.requirements)) {
      g += Math.max(0, (v ?? 0) - baseStats[s as keyof CharacterStats]);
    }
    return g;
  };

  const unlockedDons = all.filter((d) => unlocked.has(d.id));
  const lockedDons = all
    .filter((d) => !unlocked.has(d.id))
    .sort((a, b) => gap(a) - gap(b));

  if (unlockedDons.length === 0 && lockedDons.length === 0) {
    container.append(el("div", { class: "don-row don-empty" }, "Aucun don"));
    return;
  }
  // Unlocked first (gold ✦), then the full locked roadmap (🔒 + requirement),
  // closest to unlock first — so every stat's next don is visible.
  for (const d of unlockedDons) {
    container.append(
      el("div", { class: "don-row don-unlocked", title: d.desc }, `✦ ${d.name}`),
    );
  }
  for (const d of lockedDons) {
    container.append(
      el(
        "div",
        { class: "don-row don-next", title: d.desc },
        `🔒 ${d.name} — ${reqStr(d)}`,
      ),
    );
  }
}

function createSheet(character: Character, cb: SheetCallbacks): SheetHandles {
  const initialStats = character.stats;
  // M17 — use the persisted banked pools (authoritative); the first selfStats
  // tick refines them with affix flats within ~50 ms.
  const initialMaxHp = character.maxHpBase;
  const initialMaxMana = character.maxManaBase;

  const levelEl = el("span", { class: "sheet-level-num" }, String(character.level));
  const pointsBadge = el("span", { class: "sheet-points hidden" }, "0 pts");
  const xpFill = el("div", { class: "bar-fill" });
  const xpLabel = el("span", { class: "bar-label" });
  const hpFill = el("div", { class: "bar-fill" });
  const hpLabel = el("span", { class: "bar-label" });
  // M8 — single mana pool.
  const manaFill = el("div", { class: "bar-fill" });
  const manaLabel = el("span", { class: "bar-label" });
  const weightFill = el("div", { class: "bar-fill" });
  const weightLabel = el("span", { class: "bar-label" });
  const weightBar = el("div", { class: "bar bar-weight" }, weightFill, weightLabel);
  const zoneEl = el("div", { class: "sheet-zone" });
  const carryFoot = el("div", { class: "stat-foot" });

  // M17 — no level cap: there is always a next level.
  const initialXpToNext = xpToReach(character.level);
  applyXpBar(xpFill, xpLabel, character.level, character.xp, initialXpToNext);
  applyBar(hpFill, hpLabel, "HP", character.hp, initialMaxHp);
  applyBar(manaFill, manaLabel, "Mana", character.mana, initialMaxMana);
  applyWeightBar(weightBar, weightFill, weightLabel, 0, deriveCarryCapacity(initialStats));
  carryFoot.textContent = `Carry capacity: ${deriveCarryCapacity(initialStats)} kg`;
  zoneEl.textContent = `📍 ${character.zoneId} · (${character.position.x}, ${character.position.y})`;

  const statRows = new Map<
    StatKey,
    { val: HTMLElement; btn: HTMLButtonElement; cell: HTMLElement }
  >();
  const statBlock = el("div", { class: "stats-grid" });
  for (const key of STAT_KEYS) {
    const val = el("span", { class: "stat-val" }, String(initialStats[key]));
    const btn = el(
      "button",
      { class: "stat-plus", type: "button", title: statBankingHint(key) },
      "+",
    ) as HTMLButtonElement;
    btn.disabled = (character.unspentPoints ?? 0) <= 0;
    btn.addEventListener("click", () => cb.onAllocate(key));
    const cell = el(
      "div",
      { class: "stat-cell" },
      el("span", { class: "stat-key" }, key.toUpperCase()),
      val,
      btn,
    );
    statBlock.append(cell);
    statRows.set(key, { val, btn, cell });
  }
  statBlock.append(carryFoot);

  const wealthVal = el("span", { class: "wealth-val" }, formatCurrency(character.copper ?? 0));
  const wealthRow = el(
    "div",
    { class: "wealth-row" },
    el("span", { class: "wealth-key" }, "Wealth"),
    wealthVal,
  );

  const derivedDef = el("span", { class: "derived-val" }, "0");
  const derivedCrit = el("span", { class: "derived-val" }, "0%");
  const derivedResPhys = el("span", { class: "derived-val" }, "0%");
  // M8 — 6 magic-school resists. Each gets its own row.
  const derivedResDivine = el("span", { class: "derived-val" }, "0%");
  const derivedResNecro = el("span", { class: "derived-val" }, "0%");
  const derivedResWater = el("span", { class: "derived-val" }, "0%");
  const derivedResFire = el("span", { class: "derived-val" }, "0%");
  const derivedResAir = el("span", { class: "derived-val" }, "0%");
  const derivedResEarth = el("span", { class: "derived-val" }, "0%");
  const derivedDmg = el("span", { class: "derived-val" }, "");
  const derivedDmgRow = el(
    "div",
    { class: "derived-row hidden" },
    el("span", { class: "derived-key" }, "Damage"),
    derivedDmg,
  );
  function resRow(label: string, val: HTMLElement): HTMLElement {
    return el(
      "div",
      { class: "derived-row" },
      el("span", { class: "derived-key" }, label),
      val,
    );
  }
  const derivedBlock = el(
    "div",
    { class: "derived-block" },
    el(
      "div",
      { class: "derived-row" },
      el("span", { class: "derived-key" }, "Defense"),
      derivedDef,
    ),
    el(
      "div",
      { class: "derived-row" },
      el("span", { class: "derived-key" }, "Crit"),
      derivedCrit,
    ),
    resRow("Resist phys", derivedResPhys),
    resRow("Resist divine", derivedResDivine),
    resRow("Resist necro", derivedResNecro),
    resRow("Resist water", derivedResWater),
    resRow("Resist fire", derivedResFire),
    resRow("Resist air", derivedResAir),
    resRow("Resist earth", derivedResEarth),
    derivedDmgRow,
  );

  const levelRow = el(
    "div",
    { class: "sheet-row sheet-level-row" },
    el("span", { class: "sheet-level" }, "Level "),
    levelEl,
    pointsBadge,
  );

  // M17.4 — Dons panel: unlocked dons + the next one to reach.
  const donsList = el("div", { class: "dons-list" });
  const donsBlock = el(
    "div",
    { class: "dons-block" },
    el("div", { class: "dons-header" }, "Dons"),
    donsList,
  );

  const root = el("aside", { class: "sheet" });
  root.append(
    el("h3", { class: "sheet-title" }, character.name),
    levelRow,
    el("div", { class: "bar bar-xp" }, xpFill, xpLabel),
    el("div", { class: "bar bar-hp" }, hpFill, hpLabel),
    el("div", { class: "bar bar-mana" }, manaFill, manaLabel),
    weightBar,
    statBlock,
    derivedBlock,
    donsBlock,
    wealthRow,
    zoneEl,
  );

  return {
    root,
    setStats(p) {
      levelEl.textContent = String(p.level);
      if (p.unspentPoints > 0) {
        pointsBadge.textContent = `${p.unspentPoints} pt${p.unspentPoints === 1 ? "" : "s"}`;
        pointsBadge.classList.remove("hidden");
      } else {
        pointsBadge.classList.add("hidden");
      }
      applyXpBar(xpFill, xpLabel, p.level, p.xp, p.xpToNext);
      applyBar(hpFill, hpLabel, "HP", p.hp, p.maxHp);
      applyBar(manaFill, manaLabel, "Mana", p.mana, p.maxMana);
      const canSpend = p.unspentPoints > 0 && p.lifeState === "alive";
      for (const key of STAT_KEYS) {
        const row = statRows.get(key);
        if (!row) continue;
        const base = p.stats[key];
        const eff = p.effectiveStats[key];
        const bonus = eff - base;
        if (bonus !== 0) {
          row.val.innerHTML = `${eff} <span class="stat-bonus">(${bonus > 0 ? "+" : ""}${bonus})</span>`;
        } else {
          row.val.textContent = String(base);
        }
        row.btn.disabled = !canSpend;
      }
      carryFoot.textContent = `Carry capacity: ${p.maxWeight} kg`;
      applyWeightBar(weightBar, weightFill, weightLabel, p.currentWeight, p.maxWeight);
      derivedDef.textContent = String(p.defense);
      derivedCrit.textContent = formatPct(p.effectiveStats.dex * 0.005 + p.critPct);
      derivedResPhys.textContent = formatPct(p.resistPhysPct);
      derivedResDivine.textContent = formatPct(p.resistDivinePct);
      derivedResNecro.textContent = formatPct(p.resistNecromancyPct);
      derivedResWater.textContent = formatPct(p.resistWaterPct);
      derivedResFire.textContent = formatPct(p.resistFirePct);
      derivedResAir.textContent = formatPct(p.resistAirPct);
      derivedResEarth.textContent = formatPct(p.resistEarthPct);
      renderDons(donsList, p.unlockedPassives, p.stats);
      derivedDmgRow.classList.toggle(
        "hidden",
        p.meleeDmgPct === 0 && p.rangedDmgPct === 0 && p.spellDmgPct === 0,
      );
      derivedDmg.textContent = formatDmgBonuses(p.meleeDmgPct, p.rangedDmgPct, p.spellDmgPct);
      wealthVal.textContent = formatCurrency(p.copper);
      root.classList.toggle("in-combat", p.inCombat);
      root.classList.toggle("dead", p.lifeState === "dead");
    },
    setZone(zoneId, pos) {
      zoneEl.textContent = `📍 ${zoneId} · (${pos.x}, ${pos.y})`;
    },
  };
}

function formatPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatDmgBonuses(melee: number, ranged: number, spell: number): string {
  const parts: string[] = [];
  if (melee > 0) parts.push(`+${Math.round(melee * 100)}% melee`);
  if (ranged > 0) parts.push(`+${Math.round(ranged * 100)}% ranged`);
  if (spell > 0) parts.push(`+${Math.round(spell * 100)}% spell`);
  return parts.join(", ");
}

function applyBar(
  fill: HTMLElement,
  label: HTMLElement,
  name: string,
  current: number,
  max: number,
): void {
  const ratio = max > 0 ? Math.max(0, Math.min(100, Math.round((current / max) * 100))) : 0;
  fill.style.width = `${ratio}%`;
  label.textContent = `${name} ${Math.round(current)}/${max}`;
}

function applyXpBar(
  fill: HTMLElement,
  label: HTMLElement,
  level: number,
  xp: number,
  xpToNext: number,
): void {
  // M17 — no level cap; there is always a next level to fill toward.
  const ratio = xpToNext > 0 ? Math.max(0, Math.min(100, Math.round((xp / xpToNext) * 100))) : 0;
  fill.style.width = `${ratio}%`;
  label.textContent = `XP ${Math.floor(xp)}/${xpToNext}`;
}

function applyWeightBar(
  bar: HTMLElement,
  fill: HTMLElement,
  label: HTMLElement,
  current: number,
  max: number,
): void {
  const ratio = max > 0 ? current / max : 0;
  const pctClamp = Math.max(0, Math.min(100, Math.round(ratio * 100)));
  fill.style.width = `${pctClamp}%`;
  // Weight has no movement effect now; "Full" just means you can't pick up
  // more until you drop or sell something.
  const full = isEncumberedFull(current, max);
  const suffix = full ? " · Full" : "";
  label.textContent = `${current.toFixed(1)}/${max} kg${suffix}`;
  bar.classList.toggle("bar-weight-warn", ratio > ENCUMBRANCE_WARN_PCT && !full);
  bar.classList.toggle("bar-weight-full", full);
}
